import { C } from '../colors';
import type { TeoPDF } from '../TeoPDF';
import type { Bounds, TextOptions } from './types';

export function getTextWidth(this: TeoPDF, text: string, options?: TextOptions) {
  return this.doc.widthOfString(text, options);
}

export function getMaxTextWidth(this: TeoPDF, texts: (string | undefined)[]) {
  return Math.max(...texts.map((text) => this.getTextWidth(text ?? '')));
}

export function addHeadline(
  this: TeoPDF,
  text: string,
  { y = this.doc.y, x = this.doc.x }: { y?: number; x?: number } = {},
) {
  this.doc
    .font('Helvetica-Bold')
    .fillColor(C.Text)
    .fontSize(16)
    .text(text, x, y, { align: 'left' })
    .font('Helvetica')
    .moveDown(0.5);
  return this;
}

export function addText(
  this: TeoPDF,
  text: string,
  options: TextOptions = {},
  {
    onBounds,
  }: {
    onBounds?: (bounds: Bounds) => void;
  } = {},
) {
  const { pos, size, font, color, ...rest } = options;
  if (font) {
    this.doc.font(font);
  }
  if (size) {
    this.doc.fontSize(size as number); // type is probably wrong
  }
  if (color) {
    this.doc.fill(color);
  }
  if (onBounds) {
    const bounds = this.getBoundsOfString(text, rest);
    onBounds(bounds);
  }

  this.doc.text(text, pos?.x ?? this.doc.x, pos?.y ?? this.doc.y, rest);

  return this;
}

export function getBoundsOfString(this: TeoPDF, text: string, options: TextOptions): Bounds {
  // @ts-expect-error -- this is missing in the types
  return this.doc.boundsOfString(text, options) as {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export function distributedTextLine(
  this: TeoPDF,
  text: string[],
  options?: TextOptions & { margin?: number },
) {
  const { margin = 0, ...textOptions } = options ?? {};
  const origin = this.position;
  const textWidths = text.map((t) => this.getTextWidth(t, textOptions));
  const totalWidth = textWidths.reduce((acc, width) => acc + width, 0);
  const docBounds = this.getPageBounds();
  const distributedWidth = (docBounds.width - totalWidth - margin * 2) / (text.length - 1);

  const textPos = { x: origin.x, y: origin.y };
  for (let i = 0; i < text.length; i++) {
    this.addText(text[i] ?? '', { pos: textPos, ...textOptions });
    textPos.x += (textWidths[i] ?? 0) + distributedWidth;
  }
  return this;
}
